"use client";

import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { UnifiedErrorPanel } from "@/components/errors/UnifiedErrorPanel";
import type { RecentActivity } from "@/hooks/useMetricsRecentActivity";
import type { TopError } from "@/hooks/useMetricsTopErrors";
import { formatRelativeTime } from "@/lib/utils";
import { getWorkflowEditUrl } from "@/lib/utils/urlHelpers";
import { IconWarningSign } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import Link from "next/link";
import { useMemo, useState } from "react";
interface TopErrorsTableProps {
  errors: TopError[];
  isLoading?: boolean;
  recentActivities?: RecentActivity[];
  hideHeader?: boolean;
}

/**
 * Decode HTML entities like &#34; back to their characters.
 * Pure regex — identical output on server and client to avoid hydration mismatch.
 */
function decodeHtmlEntities(str: string): string {
  return str.replace(/&#(\d+);/g, (match, code) => {
    const n = Number(code);
    return n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : match;
  }).replace(/&#x([0-9a-fA-F]+);/g, (match, code) => {
    const n = parseInt(code, 16);
    return n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : match;
  }).replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
}
export function TopErrorsTable({
  errors,
  isLoading,
  recentActivities,
  hideHeader
}: TopErrorsTableProps) {
  const [selectedError, setSelectedError] = useState<TopError | null>(null);

  // Find workflows impacted by the selected error by cross-referencing recent activity
  const impactedWorkflows = useMemo(() => {
    if (!selectedError || !recentActivities) return [];
    const decodedError = decodeHtmlEntities(selectedError.error);
    const matching = recentActivities.filter(a => a.error && (a.error === selectedError.error || a.error === decodedError));

    // Deduplicate by workflowId
    const seen = new Set<string>();
    return matching.reduce<{
      workflowId: string;
      workflowName: string;
      runId: string;
      startedAt: string;
    }[]>((acc, a_0) => {
      if (!seen.has(a_0.workflowId)) {
        seen.add(a_0.workflowId);
        acc.push({
          workflowId: a_0.workflowId,
          workflowName: a_0.workflowName,
          runId: a_0.runId,
          startedAt: a_0.startedAt
        });
      }
      return acc;
    }, []);
  }, [selectedError, recentActivities]);
  if (isLoading) {
    return <div className={hideHeader ? "" : "border-ds-border-default bg-ds-surface-card rounded-xl border p-6"}>
        {!hideHeader && <h3 className="mb-4 flex items-center gap-2 text-xl font-semibold">
            <IconWarningSign className="h-6 w-6 text-red-500" />
            Top Errors
          </h3>}
        <div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 h-64 animate-pulse rounded-lg" />
      </div>;
  }
  return <>
      <div className="flex flex-col">
        {!hideHeader && <div className="mb-3 flex items-center gap-2">
            <IconWarningSign className="h-5 w-5 text-red-500" />
            <h3 className="text-ds-text-primary text-lg font-semibold">Top Errors</h3>
          </div>}
        {errors.length === 0 ? <div className="body-sm flex h-20 items-center justify-center">No errors in this time period</div> : <div className="space-y-2">
            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 px-3 pb-1">
              <span className="table-header">Error</span>
              <span className="table-header w-12 text-right">Count</span>
              <span className="table-header w-20 text-right">Last seen</span>
            </div>
            {errors.map(error => <button key={error.error} type="button" className="rounded-ds-sm shadow-ds-soft border-ds-border-default dark:hover:bg-ds-neutral-800/70 bg-ds-surface-card hover:bg-ds-surface-hover grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 border px-3 py-3 text-left transition-colors" onClick={() => setSelectedError(error)}>
                <span className="text-ds-text-secondary truncate font-mono text-xs">
                  {decodeHtmlEntities(error.error)}
                </span>
                <span className="text-ds-text-primary w-12 text-right text-sm font-medium tabular-nums">
                  {error.count}
                </span>
                <span className="label-xs w-20 whitespace-nowrap text-right">
                  {formatRelativeTime(new Date(error.lastOccurred))}
                </span>
              </button>)}
          </div>}
      </div>

      {/* Error Detail Modal */}
      <ResponsiveModal open={!!selectedError} onOpenChange={open => !open && setSelectedError(null)} title="Error Details" contentClass="max-h-[85vh] max-w-2xl">
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div>
            <h3 className="text-ds-text-primary flex items-center gap-2 text-lg font-semibold">
              <IconWarningSign className="h-4 w-4 text-red-500" />
              Error Details
            </h3>
            <p className="text-ds-text-muted mt-1 text-sm">
              Occurred {selectedError?.count} {selectedError?.count === 1 ? "time" : "times"} — last seen{" "}
              {selectedError ? formatRelativeTime(new Date(selectedError.lastOccurred)) : ""}
            </p>
          </div>

          {/* Error Display */}
          {selectedError && <UnifiedErrorPanel error={decodeHtmlEntities(selectedError.error)} variant="modal" isTestRun={false} showVersionCompare={false} />}

          {/* Impacted Workflows */}
          {recentActivities && <div>
              <span className="label label-xs mb-2 block">Impacted Workflows</span>
              {impactedWorkflows.length > 0 ? <div className="space-y-2">
                  {impactedWorkflows.map(w => <Link key={w.workflowId} href={getWorkflowEditUrl(w.workflowId)} className="border-ds-border-default dark:hover:bg-ds-neutral-800 hover:bg-ds-surface-hover flex items-center justify-between rounded-lg border px-3 py-2 transition-colors" onClick={() => setSelectedError(null)}>
                      <span className="body-sm-medium">{w.workflowName}</span>
                      <span className="label-xs">{formatRelativeTime(new Date(w.startedAt))}</span>
                    </Link>)}
                </div> : <p className="text-ds-text-muted text-sm">No matching workflows found in recent activity</p>}
            </div>}
        </div>
      </ResponsiveModal>
    </>;
}